import React, { useState, useEffect, useRef } from 'react';

// Modal component for capturing and saving a signature
const SignatureModal = ({ isOpen, onClose, onSave, index }) => {
  // Reference to the canvas element
  const canvasRef = useRef(null);
  // State to track if the user is currently drawing
  const [drawing, setDrawing] = useState(false);

  // Effect to initialize and resize the canvas when the modal opens
  useEffect(() => {
    if (isOpen) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      // Resize the canvas to fit its container
      const resizeCanvas = () => {
        canvas.width = canvas.offsetWidth;
        canvas.height = 200;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      };

      resizeCanvas();
      window.addEventListener('resize', resizeCanvas);

      // Cleanup event listener on component unmount or modal close
      return () => window.removeEventListener('resize', resizeCanvas);
    }
  }, [isOpen]);

  // Get the mouse or touch position relative to the canvas
  const getPosition = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
  };

  // Start drawing on mouse down or touch start
  const startDraw = (e) => {
    setDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPosition(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    e.preventDefault();
  };

  // Draw on the canvas as the mouse or touch moves
  const draw = (e) => {
    if (!drawing) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPosition(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    e.preventDefault();
  };

  // Stop drawing on mouse up or touch end
  const endDraw = (e) => {
    setDrawing(false);
    e.preventDefault();
  };

  // Clear the canvas
  const clearPad = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // Save the signature as a data URL and close the modal
  const handleSave = () => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    onSave(dataURL, index);
    onClose();
  };

  // Render nothing if the modal is not open
  if (!isOpen) return null;

  // Render the modal with a canvas for drawing the signature
  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Signature</h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
          </div>
          <div className="modal-body">
            <canvas
              ref={canvasRef}
              className="w-100 border-2 border-dashed border-gray-500 bg-white"
              style={{ touchAction: 'none' }}
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={endDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={endDraw}
            />
            <button
              type="button"
              className="mt-2 px-4 btn btn-primary py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              onClick={clearPad}
            >
              Clear
            </button>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-success" onClick={handleSave}>
              Save
            </button>
            <button
              type="button"
              className="btn bg-danger-subtle text-danger font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;